import { createContext, useContext, useState, ReactNode } from 'react';
import { useIonRouter } from '@ionic/react';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'alert';
  read: boolean;
  timestamp: string;
  link?: string;
}

interface DropdownPosition {
  top: number;
  right: number;
}

interface NotificationsContextType {
  isOpen: boolean;
  notifications: Notification[];
  unreadCount: number;
  dropdownPosition: DropdownPosition | null;
  openDropdown: (position: DropdownPosition) => void;
  closeDropdown: () => void;
  toggleDropdown: (position: DropdownPosition) => void;
  handleNotificationClick: (notification: Notification) => void;
  handleMarkAllRead: () => void;
  handleClearAll: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export type { Notification };

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const router = useIonRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const openDropdown = (position: DropdownPosition) => {
    setDropdownPosition(position);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setDropdownPosition(null);
  };

  const toggleDropdown = (position: DropdownPosition) => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown(position);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    setNotifications(prev =>
      prev.map(n => (n.id === notification.id ? { ...n, read: true } : n))
    );

    // Navigate if link exists
    if (notification.link) {
      router.push(notification.link, 'none');
    }

    closeDropdown();
  };

  const handleMarkAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
    closeDropdown();
  };

  return (
    <NotificationsContext.Provider
      value={{
        isOpen,
        notifications,
        unreadCount,
        dropdownPosition,
        openDropdown,
        closeDropdown,
        toggleDropdown,
        handleNotificationClick,
        handleMarkAllRead,
        handleClearAll,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
